import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render-time crashes so a single broken view shows a readable error
 * instead of blanking the whole app. Without a boundary, an uncaught render
 * error unmounts the entire React tree and leaves the bare dark <html>
 * background — the "black screen" failure mode.
 *
 * Wrapped around <Routes> in App.tsx and keyed by pathname, so navigating via
 * the surviving nav remounts it fresh and clears the error.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("dyno PWA render crash:", error, info.componentStack);
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="mx-auto max-w-lg p-6">
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-5">
          <h2 className="text-base font-semibold text-destructive">
            Something went wrong
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            This view hit an error and stopped rendering. The rest of the app
            is still running — switch tabs or reload.
          </p>
          <pre className="mt-3 overflow-x-auto rounded bg-background/60 p-2 text-xs text-muted-foreground">
            {error.message}
          </pre>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-4 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
